import {
  RBIM_DATA_FILE_TYPE,
  RBIM_DATA_FILE_VERSION,
  type RbimDataFile,
  type RbimQuestionnairePayloadV1,
} from "@rbim/shared";

export type LegacyOnlineImportResult = {
  dataFile: RbimDataFile;
  skippedRows: number;
  warnings: string[];
};

type LegacyRow = Record<string, string>;

const headerAliases: Record<string, string[]> = {
  province: ["province", "provincename"],
  provinceCode: ["provinceno", "provincecode", "psgcprovince"],
  municipality: ["citymunicipality", "municipalitycity", "municipality", "city", "citymunicipalityname"],
  municipalityCode: ["citymunicipalityno", "municipalitycode", "citycode", "psgcmunicipality"],
  barangay: ["barangay", "barangayname"],
  barangayCode: ["barangayno", "barangaycode", "psgcbarangay"],
  purok: ["purok", "zone", "sitio"],
  address: ["address", "residentialaddress"],
  householdNumber: ["household", "householdno", "householdnumber", "householdcode", "hhno", "hhnumber"],
  family: ["family", "familyno", "familynumber", "familyunit", "familyunitno"],
  householdHead: ["householdhead", "headofhousehold", "hhhead"],
  respondentName: ["nameofresopondent", "nameofrespondent", "respondent", "respondentname"],
  lastName: ["lname", "lastname", "surname"],
  firstName: ["fname", "firstname", "givenname"],
  middleName: ["mname", "middlename"],
  relationshipToHead: ["relationship", "relationshiptohead", "relationtohead"],
  sex: ["sex", "gender"],
  age: ["age", "ageyears"],
  birthdate: ["birthdate", "dateofbirth", "dob", "birthday"],
  civilStatus: ["civilstatus", "maritalstatus"],
  religion: ["religion"],
  ethnicity: ["ethnicity", "ethnicgroup"],
  highestEducation: ["levelofeducation", "highesteducation", "education"],
  currentlyEnrolled: ["currentlyenrolled", "enrolled"],
  occupation: ["occupation", "occupationstatus", "work", "workstatus"],
  monthlyIncome: ["monthlyincome", "monthltyincome", "income"],
  sourceOfIncome: ["sourceofincome", "incomesource"],
  registeredVoterBarangay: ["registeredvoter", "voter"],
  residentType: ["typeofresident", "residenttype", "classification"],
};

export async function parseOnlineRbimImportFile(file: File): Promise<LegacyOnlineImportResult> {
  const extension = getExtension(file.name);
  if (extension === ".rbim" || extension === ".bak") {
    return parseJsonTransferFile(file);
  }
  if (![".xlsx", ".xls", ".csv"].includes(extension)) {
    throw new Error("Unsupported file type. Select a .rbim, .bak, .xlsx, .xls, or .csv RBIM file.");
  }

  if (extension === ".xlsx") {
    const sheets = await parseXlsxSheets(await file.arrayBuffer());
    const canonical = parseCanonicalRbimWorkbook(sheets, file.name);
    if (canonical) return canonical;
    const legacySheet = selectBestLegacySheet(sheets);
    if (!legacySheet) {
      throw new Error("No RBIM data worksheet was detected. The workbook must contain resident rows with Household Number, First Name, and Last Name columns.");
    }
    return rowsToTransferFile(legacySheet.rows, file.name);
  }

  const matrix = extension === ".csv"
    ? parseCsv(await file.text())
    : parseLegacyXls(await file.arrayBuffer());
  return rowsToTransferFile(matrix, file.name);
}

async function parseJsonTransferFile(file: File): Promise<LegacyOnlineImportResult> {
  const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
  if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.records)) {
    throw new Error("The selected RBIM file does not contain a valid records array.");
  }
  const dataFile: RbimDataFile = {
    fileType: RBIM_DATA_FILE_TYPE,
    version: RBIM_DATA_FILE_VERSION,
    kind: parsed.kind === "backup" ? "backup" : "export",
    createdAt: typeof parsed.createdAt === "string" ? parsed.createdAt : new Date().toISOString(),
    records: parsed.records as RbimDataFile["records"],
  };
  return {
    dataFile,
    skippedRows: 0,
    warnings: file.name.toLowerCase().endsWith(".bak")
      ? ["Legacy RBIM backup normalized for record-level online import. Existing server records are not replaced."]
      : [],
  };
}

function rowsToTransferFile(matrix: string[][], fileName: string): LegacyOnlineImportResult {
  const rows = matrix.filter((row) => row.some((cell) => clean(cell)));
  if (rows.length < 2) throw new Error("The spreadsheet does not contain RBIM data rows.");
  const headerIndex = findHeaderRow(rows);
  const headers = rows[headerIndex].map(normalizeHeader);
  const fieldIndexes = buildFieldIndexes(headers);
  if (fieldIndexes.householdNumber === undefined || fieldIndexes.firstName === undefined || fieldIndexes.lastName === undefined) {
    throw new Error("Old RBIM columns were not detected. The file must include Household, FName/First Name, and LName/Last Name columns.");
  }

  const warnings: string[] = [];
  const normalizedRows: LegacyRow[] = [];
  let skippedRows = 0;
  for (const values of rows.slice(headerIndex + 1)) {
    const row = buildLegacyRow(values, fieldIndexes);
    if (!clean(row.householdNumber) || !clean(row.firstName) || !clean(row.lastName)) {
      skippedRows += 1;
      continue;
    }
    if (!parseBirthdate(row.birthdate) && parseAge(row.age) === null) {
      skippedRows += 1;
      continue;
    }
    normalizedRows.push(row);
  }
  if (normalizedRows.length === 0) {
    throw new Error("No importable legacy RBIM resident rows were found. Resident name, household number, and birthdate or age are required.");
  }
  if (skippedRows) warnings.push(`${skippedRows.toLocaleString()} legacy row(s) were skipped because required resident data was incomplete.`);

  const groups = new Map<string, LegacyRow[]>();
  for (const row of normalizedRows) {
    const { householdCode } = parseHouseholdReference(row.householdNumber);
    const locationKey = [row.municipalityCode || row.municipality, row.barangayCode || row.barangay].map(normalizeHeader).join("|");
    const key = `${locationKey}|${normalizeHeader(householdCode)}`;
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  }

  const records = Array.from(groups.values()).map((group) => ({
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    municipalityCode: clean(group[0].municipalityCode),
    barangayCode: clean(group[0].barangayCode),
    cleanedData: {
      legacyImport: true,
      sourceFileName: fileName,
    },
    rawData: buildPayload(group),
  }));

  warnings.push("Legacy spreadsheet values were normalized into the current RBIM questionnaire schema. Review imported records after migration, especially fields that did not exist in the old RBIM format.");
  return {
    dataFile: {
      fileType: RBIM_DATA_FILE_TYPE,
      version: RBIM_DATA_FILE_VERSION,
      kind: "export",
      createdAt: new Date().toISOString(),
      records,
    },
    skippedRows,
    warnings,
  };
}

function buildPayload(rows: LegacyRow[]): RbimQuestionnairePayloadV1 {
  const first = rows[0];
  const household = parseHouseholdReference(first.householdNumber);
  const memberDrafts = rows.map((row, index) => buildMember(row, index, household.family));
  let headIndex = memberDrafts.findIndex((member) => isHead(member.relationshipToHead));
  if (headIndex < 0) {
    const declaredHead = normalizePersonName(first.householdHead);
    headIndex = memberDrafts.findIndex((member) => normalizePersonName(`${member.firstName} ${member.middleName} ${member.lastName}`) === declaredHead);
  }
  if (headIndex < 0) headIndex = 0;
  const members = memberDrafts.map((member, index) => ({
    ...member,
    relationshipToHead: index === headIndex ? "Head" : isHead(member.relationshipToHead) ? "Member" : member.relationshipToHead,
  }));
  const head = members[headIndex];
  const householdHead = clean(first.householdHead) || `${head.firstName} ${head.middleName} ${head.lastName}`.replace(/\s+/g, " ").trim();
  const family = household.family;
  let familyHeadAssigned = false;
  for (const member of members) {
    if (member.familyName === family && (!familyHeadAssigned && member.relationshipToHead === "Head")) {
      member.isFamilyHead = true;
      member.relationshipToFamilyHead = "Head";
      familyHeadAssigned = true;
    }
  }
  if (!familyHeadAssigned && members[0]) {
    members[0].isFamilyHead = true;
    members[0].relationshipToFamilyHead = "Head";
  }

  return {
    household: {
      province: clean(first.province) || "UNKNOWN - LEGACY IMPORT",
      provinceCode: clean(first.provinceCode),
      municipality: clean(first.municipality),
      municipalityCode: clean(first.municipalityCode),
      barangay: clean(first.barangay),
      barangayCode: clean(first.barangayCode),
      purok: clean(first.purok),
      address: clean(first.address) || [clean(first.purok), clean(first.barangay)].filter(Boolean).join(", ") || "LEGACY RBIM IMPORT",
      householdCode: household.householdCode,
      family,
      householdType: "Household",
      respondentName: clean(first.respondentName) || householdHead,
      householdHead,
      totalMembers: String(members.length),
    },
    householdQuestions: {
      housingTenure: "", lotTenure: "", hazardProneArea: "", disasterExperiencedPastFiveYears: "",
      knowsNearestEvacuationCenter: "", lightingFuel: "", cookingFuel: "", drinkingWaterSource: "",
      garbageDisposal: "", segregatesGarbage: "", toiletFacility: "", buildingType: "", wallMaterial: "",
      femaleDeathPast12Months: "", femaleDeathAge: "", femaleDeathCause: "", childDeathPast12Months: "",
      childDeathAge: "", childDeathSex: "", childDeathCause: "", commonDiseaseCause1: "", commonDiseaseCause2: "",
      commonDiseaseCause3: "", primaryNeed1: "", primaryNeed2: "", primaryNeed3: "", futureBarangay: "",
      futureMunicipality: "", futureProvince: "",
    },
    interviews: [emptyInterview(), emptyInterview()],
    encoding: { dateEncoded: "", encoder: "LEGACY RBIM IMPORT", supervisor: "" },
    members,
  };
}

function buildMember(row: LegacyRow, index: number, fallbackFamily: RbimQuestionnairePayloadV1["household"]["family"]): RbimQuestionnairePayloadV1["members"][number] {
  const exactBirthdate = parseBirthdate(row.birthdate);
  const age = parseAge(row.age);
  const family = normalizeFamily(row.family || parseHouseholdReference(row.householdNumber).family || fallbackFamily);
  const dateUnknown = !exactBirthdate;
  return {
    firstName: clean(row.firstName), middleName: clean(row.middleName), lastName: clean(row.lastName),
    sex: clean(row.sex) || "99 - N/A", dateOfBirth: exactBirthdate ?? "",
    birthdatePrecision: exactBirthdate ? "exact" : "age-only",
    birthdateOriginalValue: clean(row.birthdate) || undefined,
    birthYear: exactBirthdate ? Number(exactBirthdate.slice(-4)) : null,
    birthMonth: exactBirthdate ? Number(exactBirthdate.slice(0, 2)) : null,
    birthDay: exactBirthdate ? Number(exactBirthdate.slice(3, 5)) : null,
    importedAge: dateUnknown ? age : null,
    importedAgeAsOf: dateUnknown ? new Date().toISOString().slice(0, 10) : null,
    birthdateSource: "legacy-import",
    dateOfBirthUnknown: dateUnknown,
    reportedAge: dateUnknown ? age : null,
    ageAsOfDate: dateUnknown ? new Date().toISOString().slice(0, 10) : null,
    ageSource: dateUnknown ? "reported" : "date-of-birth",
    relationshipToHead: clean(row.relationshipToHead) || (index === 0 ? "Head" : "Member"),
    familyUnitAssignment: "same",
    familyUnitId: `FAMILY-${family}`,
    familyUnitNo: family,
    familyName: family,
    relationshipToFamilyHead: "Member",
    isFamilyHead: false,
    civilStatus: clean(row.civilStatus), placeOfBirth: "99 - N/A", nationality: "99 - N/A", nationalityOther: "",
    religion: clean(row.religion), ethnicity: clean(row.ethnicity), highestEducation: clean(row.highestEducation),
    currentlyEnrolled: clean(row.currentlyEnrolled), schoolLevel: "", placeOfSchool: "", reasonNotAttendingSchool: "",
    reasonNotAttendingSchoolOther: "", monthlyIncome: clean(row.monthlyIncome), sourceOfIncome: clean(row.sourceOfIncome),
    sourceOfIncomeOther: "", workStatus: clean(row.occupation), placeOfWork: "", placeOfDelivery: "", placeOfDeliveryOther: "",
    birthAttendant: "", birthAttendantOther: "", immunization: "", livingChildren: "", livingChildrenAlive: "",
    familyPlanningUse: "", familyPlanningUseOther: "", fpMethodSource: "", fpMethodSourceOther: "", intentionToUseFp: "",
    intentionToUseFpDetail: "", healthInsurance: "", healthInsuranceOther: "", facilityVisitedPast12Months: "",
    facilityVisitedPast12MonthsOther: "", healthFacilityVisitReason: "", healthFacilityVisitReasonOther: "", disability: "",
    soloParentStatus: "", registeredSeniorCitizen: "", registeredVoterBarangay: clean(row.registeredVoterBarangay),
    previousResidenceFiveYears: "", previousResidenceSixMonths: "", lengthOfStayInBarangay: "", residentType: clean(row.residentType),
    dateOfTransfer: "", reasonLeavingPreviousResidence1: "", reasonLeavingPreviousResidence1Other: "",
    reasonLeavingPreviousResidence2: "", reasonLeavingPreviousResidence2Other: "", reasonLeavingPreviousResidence3: "",
    reasonLeavingPreviousResidence3Other: "", returnToPreviousResidence: "", returnToPreviousResidenceDetail: "",
    transferReason1: "", transferReason1Other: "", transferReason2: "", transferReason2Other: "", transferReason3: "",
    transferReason3Other: "", durationOfStayCurrentBarangay: "", durationOfStayCurrentBarangayDetail: "", hasValidCtc: "",
    ctcIssuedInBarangay: "", skillsTrainingInterest: "", skills: "", skillsOther: "", occupation: clean(row.occupation),
  };
}

function emptyInterview(): RbimQuestionnairePayloadV1["interviews"][number] {
  return { visit: "1", dateOfVisit: "", timeStart: "", timeEnd: "", result: "", dateOfNextVisit: "", interviewer: "", supervisor: "" };
}

function buildFieldIndexes(headers: string[]) {
  const result: Record<string, number | undefined> = {};
  for (const [field, aliases] of Object.entries(headerAliases)) {
    const index = headers.findIndex((header) => aliases.includes(header));
    if (index >= 0) result[field] = index;
  }
  return result;
}

function buildLegacyRow(values: string[], indexes: Record<string, number | undefined>): LegacyRow {
  const result: LegacyRow = {};
  for (const [field, index] of Object.entries(indexes)) result[field] = index === undefined ? "" : clean(values[index]);
  return result;
}

function findHeaderRow(rows: string[][]) {
  let bestIndex = 0;
  let bestScore = -1;
  rows.slice(0, 25).forEach((row, index) => {
    const normalized = row.map(normalizeHeader);
    const score = Object.values(headerAliases).filter((aliases) => normalized.some((header) => aliases.includes(header))).length;
    if (score > bestScore) { bestScore = score; bestIndex = index; }
  });
  return bestIndex;
}

function parseHouseholdReference(value: string) {
  const raw = clean(value).toUpperCase();
  const match = raw.match(/^(.*?)[\s/_-]+([A-J])$/);
  return { householdCode: clean(match?.[1] ?? raw), family: normalizeFamily(match?.[2] ?? "A") };
}

function normalizeFamily(value: string): RbimQuestionnairePayloadV1["household"]["family"] {
  const letter = clean(value).toUpperCase().match(/[A-J]/)?.[0] ?? "A";
  return letter as RbimQuestionnairePayloadV1["household"]["family"];
}

function isHead(value: string) {
  return /^(?:1\s*[-.]?\s*)?(?:head|household head|hh head)$/i.test(clean(value));
}

function normalizePersonName(value: string) { return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim(); }
function normalizeHeader(value: string) { return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, ""); }
function clean(value: unknown) { return String(value ?? "").replace(/\u00a0/g, " ").trim(); }
function getExtension(name: string) { return name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ""; }

function parseAge(value: string) {
  const match = clean(value).match(/\d{1,3}/);
  if (!match) return null;
  const age = Number(match[0]);
  return Number.isInteger(age) && age >= 0 && age <= 120 ? age : null;
}

function parseBirthdate(value: string) {
  const text = clean(value);
  if (!text) return null;
  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const serial = Number(text);
    if (serial >= 1 && serial <= 100000) {
      const date = new Date(Date.UTC(1899, 11, 30) + Math.round(serial) * 86400000);
      return formatDate(date);
    }
  }
  const parts = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (parts) {
    const date = new Date(Number(parts[3]), Number(parts[1]) - 1, Number(parts[2]));
    if (date.getFullYear() === Number(parts[3]) && date.getMonth() === Number(parts[1]) - 1 && date.getDate() === Number(parts[2])) return formatDate(date);
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : formatDate(parsed);
}

function formatDate(date: Date) {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${date.getFullYear()}`;
}

function parseCsv(text: string) {
  const rows: string[][] = [];
  let row: string[] = [], cell = "", quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (quoted && text[i + 1] === '"') { cell += '"'; i += 1; } else quoted = !quoted;
    } else if (ch === "," && !quoted) { row.push(cell); cell = ""; }
    else if ((ch === "\n" || ch === "\r") && !quoted) {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell); if (row.some((value) => value.length)) rows.push(row); row = []; cell = "";
    } else cell += ch;
  }
  row.push(cell); if (row.some((value) => value.length)) rows.push(row);
  return rows;
}

function parseLegacyXls(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  if (!/<table[\s>]/i.test(text)) {
    throw new Error("This .xls file uses the older binary Excel format. Save it as .xlsx or .csv, then import it again.");
  }
  const document = new DOMParser().parseFromString(text, "text/html");
  return Array.from(document.querySelectorAll("tr")).map((row) =>
    Array.from(row.querySelectorAll("th,td")).map((cell) => clean(cell.textContent)));
}

type XlsxSheet = { name: string; rows: string[][] };

async function parseXlsxSheets(buffer: ArrayBuffer): Promise<XlsxSheet[]> {
  const entries = await unzip(buffer);
  const sharedStrings = parseSharedStrings(entries.get("xl/sharedStrings.xml"));
  const sheetEntries = Array.from(entries.keys())
    .filter((name) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(name))
    .sort((left, right) => {
      const leftNo = Number(left.match(/sheet(\d+)\.xml/i)?.[1] ?? 0);
      const rightNo = Number(right.match(/sheet(\d+)\.xml/i)?.[1] ?? 0);
      return leftNo - rightNo;
    });

  if (!sheetEntries.length) throw new Error("The .xlsx file does not contain a readable worksheet.");

  const workbookNames = parseWorkbookSheetNames(entries.get("xl/workbook.xml"));
  return sheetEntries.map((sheetPath, index) => ({
    name: workbookNames[index] ?? `Sheet ${index + 1}`,
    rows: parseWorksheetRows(entries.get(sheetPath), sharedStrings),
  }));
}

function parseWorksheetRows(bytes: Uint8Array | undefined, sharedStrings: string[]) {
  if (!bytes) return [];
  const xml = new TextDecoder().decode(bytes);
  const document = new DOMParser().parseFromString(xml, "application/xml");
  return Array.from(document.querySelectorAll("row")).map((row) => {
    const values: string[] = [];
    for (const cell of Array.from(row.querySelectorAll("c"))) {
      const reference = cell.getAttribute("r") ?? "A1";
      const index = columnIndex(reference.replace(/\d+/g, ""));
      const type = cell.getAttribute("t");
      const raw = clean(cell.querySelector("v")?.textContent ?? Array.from(cell.querySelectorAll("is t")).map((node) => node.textContent ?? "").join(""));
      values[index] = type === "s" ? sharedStrings[Number(raw)] ?? "" : raw;
    }
    return values.map((value) => value ?? "");
  });
}

function parseWorkbookSheetNames(bytes?: Uint8Array) {
  if (!bytes) return [];
  const document = new DOMParser().parseFromString(new TextDecoder().decode(bytes), "application/xml");
  return Array.from(document.querySelectorAll("sheet")).map((sheet) => clean(sheet.getAttribute("name")));
}


function selectBestLegacySheet(sheets: XlsxSheet[]) {
  let best: { sheet: XlsxSheet; score: number } | null = null;

  for (const sheet of sheets) {
    const rows = sheet.rows.filter((row) => row.some((cell) => clean(cell)));
    if (!rows.length) continue;

    let sheetBestScore = -1;
    for (const row of rows.slice(0, 25)) {
      const normalized = row.map(normalizeHeader);
      const score = Object.values(headerAliases)
        .filter((aliases) => normalized.some((header) => aliases.includes(header)))
        .length;

      const requiredMatches = [
        ["household", "householdno", "householdnumber", "householdcode", "hhno", "hhnumber"],
        ["fname", "firstname", "givenname"],
        ["lname", "lastname", "surname"],
      ].filter((aliases) => normalized.some((header) => aliases.includes(header))).length;

      const weightedScore = score + requiredMatches * 20;
      if (weightedScore > sheetBestScore) sheetBestScore = weightedScore;
    }

    if (!best || sheetBestScore > best.score) {
      best = { sheet, score: sheetBestScore };
    }
  }

  return best && best.score >= 60 ? best.sheet : null;
}

function parseCanonicalRbimWorkbook(sheets: XlsxSheet[], fileName: string): LegacyOnlineImportResult | null {
  const householdSheet = sheets.find((sheet) => hasCanonicalHeaders(sheet.rows, ["Household ID", "Household Number", "Province", "Barangay"]));
  const residentSheet = sheets.find((sheet) => hasCanonicalHeaders(sheet.rows, ["Household ID", "Q1 First Name", "Q1 Surname", "Q3 Sex"]));
  if (!householdSheet || !residentSheet) return null;

  const householdRows = matrixToNamedRows(householdSheet.rows);
  const residentRows = matrixToNamedRows(residentSheet.rows);
  if (!householdRows.length || !residentRows.length) {
    throw new Error("The RBIM workbook does not contain household and resident data rows.");
  }

  const residentsByHousehold = new Map<string, Array<Record<string, string>>>();
  for (const row of residentRows) {
    const householdId = valueByHeader(row, "Household ID");
    const householdNumber = valueByHeader(row, "Household Number");
    const key = normalizeHeader(householdId || householdNumber);
    if (!key) continue;
    const group = residentsByHousehold.get(key) ?? [];
    group.push(row);
    residentsByHousehold.set(key, group);
  }

  const records: RbimDataFile["records"] = [];
  let skippedRows = 0;

  for (const householdRow of householdRows) {
    const householdId = valueByHeader(householdRow, "Household ID");
    const householdNumber = valueByHeader(householdRow, "Household Number");
    const key = normalizeHeader(householdId || householdNumber);
    const residentRowsForHousehold = residentsByHousehold.get(key) ?? [];

    if (!householdNumber || residentRowsForHousehold.length === 0) {
      skippedRows += 1;
      continue;
    }

    const payload = buildCanonicalPayload(householdRow, residentRowsForHousehold);
    records.push({
      createdAt: canonicalDateTime(valueByHeader(householdRow, "Submitted At")) ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      municipalityCode: payload.household.municipalityCode,
      barangayCode: payload.household.barangayCode,
      cleanedData: {
        canonicalWorkbookImport: true,
        sourceFileName: fileName,
      },
      rawData: payload,
    });
  }

  if (!records.length) {
    throw new Error("No importable household records were found in the RBIM workbook.");
  }

  const warnings = [
    "Canonical RBIM Excel workbook detected. Household and Residents worksheets were reconstructed into current RBIM records before server validation.",
  ];
  if (skippedRows) warnings.push(`${skippedRows.toLocaleString()} household row(s) were skipped because no matching resident rows were found.`);

  return {
    dataFile: {
      fileType: RBIM_DATA_FILE_TYPE,
      version: RBIM_DATA_FILE_VERSION,
      kind: "export",
      createdAt: new Date().toISOString(),
      records,
    },
    skippedRows,
    warnings,
  };
}

function buildCanonicalPayload(
  householdRow: Record<string, string>,
  residentRows: Array<Record<string, string>>,
): RbimQuestionnairePayloadV1 {
  const members = residentRows.map(buildCanonicalMember);
  ensureSingleHouseholdHead(members);

  const householdHeadFromSheet = valueByHeader(householdRow, "Household Head");
  const headMember = members.find((member) => isHead(member.relationshipToHead)) ?? members[0];
  const computedHead = [headMember.firstName, headMember.middleName, headMember.lastName].filter(Boolean).join(" ").trim();

  return {
    household: {
      province: requiredImportText(valueByHeader(householdRow, "Province"), "UNKNOWN - IMPORT"),
      provinceCode: valueByHeader(householdRow, "Province / HUC Code"),
      municipality: requiredImportText(valueByHeader(householdRow, "City / Municipality"), "UNKNOWN - IMPORT"),
      municipalityCode: valueByHeader(householdRow, "City / Municipality Code"),
      barangay: requiredImportText(valueByHeader(householdRow, "Barangay"), "UNKNOWN - IMPORT"),
      barangayCode: valueByHeader(householdRow, "Barangay Code"),
      purok: valueByHeader(householdRow, "Purok / Sitio"),
      address: requiredImportText(valueByHeader(householdRow, "Address"), valueByHeader(householdRow, "Barangay") || "RBIM IMPORT"),
      householdCode: valueByHeader(householdRow, "Household Number"),
      family: normalizeFamily(valueByHeader(householdRow, "Household Family")),
      householdType: valueByHeader(householdRow, "Household Type") === "Institutional Living Quarter"
        ? "Institutional Living Quarter"
        : "Household",
      respondentName: requiredImportText(valueByHeader(householdRow, "Name of Respondent"), householdHeadFromSheet || computedHead),
      householdHead: requiredImportText(householdHeadFromSheet, computedHead),
      totalMembers: String(members.length),
    },
    householdQuestions: {
      housingTenure: valueByHeader(householdRow, "Q46 Housing Unit Tenure"),
      lotTenure: valueByHeader(householdRow, "Q47 Lot Tenure"),
      hazardProneArea: valueByHeader(householdRow, "Q48 Hazard-Prone Area"),
      disasterExperiencedPastFiveYears: valueByHeader(householdRow, "Q49 Disaster Experienced in Past 5 Years"),
      knowsNearestEvacuationCenter: valueByHeader(householdRow, "Q50 Knows Nearest Evacuation Center"),
      lightingFuel: valueByHeader(householdRow, "Q51 Fuel Used for Lighting"),
      cookingFuel: valueByHeader(householdRow, "Q52 Fuel Used for Cooking"),
      drinkingWaterSource: valueByHeader(householdRow, "Q53 Drinking Water Source"),
      garbageDisposal: valueByHeader(householdRow, "Q54A Kitchen Garbage Disposal"),
      segregatesGarbage: valueByHeader(householdRow, "Q54B Garbage Segregation"),
      toiletFacility: valueByHeader(householdRow, "Q55 Toilet Facility"),
      buildingType: valueByHeader(householdRow, "Q56 Building Type"),
      wallMaterial: valueByHeader(householdRow, "Q57 Wall Material"),
      femaleDeathPast12Months: valueByHeader(householdRow, "Q58 Female Death in Past 12 Months"),
      femaleDeathAge: valueByHeader(householdRow, "Q58 Female Death Age"),
      femaleDeathCause: valueByHeader(householdRow, "Q58 Female Death Cause"),
      childDeathPast12Months: valueByHeader(householdRow, "Q59 Child Death in Past 12 Months"),
      childDeathAge: valueByHeader(householdRow, "Q59 Child Death Age"),
      childDeathSex: valueByHeader(householdRow, "Q59 Child Death Sex"),
      childDeathCause: valueByHeader(householdRow, "Q59 Child Death Cause"),
      commonDiseaseCause1: valueByHeader(householdRow, "Q60 Common Disease / Cause 1"),
      commonDiseaseCause2: valueByHeader(householdRow, "Q60 Common Disease / Cause 2"),
      commonDiseaseCause3: valueByHeader(householdRow, "Q60 Common Disease / Cause 3"),
      primaryNeed1: valueByHeader(householdRow, "Q61 Primary Need 1"),
      primaryNeed2: valueByHeader(householdRow, "Q61 Primary Need 2"),
      primaryNeed3: valueByHeader(householdRow, "Q61 Primary Need 3"),
      futureBarangay: valueByHeader(householdRow, "Q62 Future Barangay"),
      futureMunicipality: valueByHeader(householdRow, "Q62 Future City / Municipality"),
      futureProvince: valueByHeader(householdRow, "Q62 Future Province"),
    },
    interviews: [
      buildCanonicalInterview(householdRow, "1st"),
      buildCanonicalInterview(householdRow, "2nd"),
    ],
    encoding: {
      dateEncoded: normalizeCalendarDate(valueByHeader(householdRow, "Date Encoded")),
      encoder: valueByHeader(householdRow, "Encoder"),
      supervisor: valueByHeader(householdRow, "Encoding Supervisor"),
    },
    members,
  };
}

function buildCanonicalInterview(row: Record<string, string>, prefix: "1st" | "2nd"): RbimQuestionnairePayloadV1["interviews"][number] {
  const result = valueByHeader(row, `${prefix} Interview Result`);
  return {
    visit: valueByHeader(row, `${prefix} Visit`) || (prefix === "1st" ? "1" : "2"),
    dateOfVisit: normalizeCalendarDate(valueByHeader(row, `${prefix} Date of Visit`)),
    timeStart: valueByHeader(row, `${prefix} Time Started`),
    timeEnd: valueByHeader(row, `${prefix} Time Ended`),
    result: result === "C" || result === "CB" || result === "R" ? result : "",
    dateOfNextVisit: normalizeCalendarDate(valueByHeader(row, `${prefix} Date of Next Visit`)),
    interviewer: valueByHeader(row, `${prefix} Interviewer`),
    supervisor: valueByHeader(row, `${prefix} Interview Supervisor`),
  };
}

function buildCanonicalMember(row: Record<string, string>): RbimQuestionnairePayloadV1["members"][number] {
  const exportedBirthdate = normalizeCalendarDate(valueByHeader(row, "Q5 Date of Birth"));
  const exportedUnknown = parseBooleanCell(valueByHeader(row, "Q5 Date of Birth Unknown"));
  const reportedAge = parseNullableAge(valueByHeader(row, "Q4 Reported Age"));
  const computedAge = parseNullableAge(valueByHeader(row, "Computed Age (Years)"));
  const dateOfBirthUnknown = exportedUnknown || !exportedBirthdate;
  const resolvedAge = dateOfBirthUnknown ? (reportedAge ?? computedAge ?? 0) : null;
  const precisionRaw = valueByHeader(row, "Q5 Birthdate Precision");
  const precision = ["exact", "month-year", "year-only", "age-only", "unknown"].includes(precisionRaw)
    ? precisionRaw as RbimQuestionnairePayloadV1["members"][number]["birthdatePrecision"]
    : dateOfBirthUnknown ? "age-only" : "exact";
  const familyUnitAssignmentRaw = valueByHeader(row, "Family Unit Assignment");
  const familyUnitAssignment = ["same", "new", "existing"].includes(familyUnitAssignmentRaw)
    ? familyUnitAssignmentRaw as "same" | "new" | "existing"
    : "same";

  return {
    firstName: valueByHeader(row, "Q1 First Name"),
    middleName: valueByHeader(row, "Q1 Middle Name"),
    lastName: valueByHeader(row, "Q1 Surname"),
    sex: requiredImportText(valueByHeader(row, "Q3 Sex"), "99 - N/A"),
    dateOfBirth: dateOfBirthUnknown ? "" : exportedBirthdate,
    birthdatePrecision: precision,
    birthdateOriginalValue: valueByHeader(row, "Q5 Date of Birth") || undefined,
    birthdateSource: "legacy-import",
    dateOfBirthUnknown,
    reportedAge: resolvedAge,
    ageAsOfDate: dateOfBirthUnknown ? (normalizeCalendarDate(valueByHeader(row, "Q4/Q5 Age As Of Date")) || todayMmDdYyyy()) : null,
    ageSource: dateOfBirthUnknown ? "reported" : "date-of-birth",
    relationshipToHead: requiredImportText(valueByHeader(row, "Q2 Relationship to Household Head"), "Member"),
    familyUnitAssignment,
    familyUnitId: valueByHeader(row, "Saved Family Unit ID") || valueByHeader(row, "Family Unit ID"),
    familyUnitNo: valueByHeader(row, "Family Unit Number") || normalizeFamily(valueByHeader(row, "Family Name")),
    familyName: valueByHeader(row, "Family Name") || normalizeFamily(valueByHeader(row, "Family Unit Number")),
    relationshipToFamilyHead: valueByHeader(row, "Relationship to Family Head"),
    isFamilyHead: parseBooleanCell(valueByHeader(row, "Is Family Head")),
    civilStatus: valueByHeader(row, "Q8 Marital Status"),
    placeOfBirth: requiredImportText(valueByHeader(row, "Q6 Place of Birth"), "99 - N/A"),
    nationality: requiredImportText(valueByHeader(row, "Q7 Nationality"), "99 - N/A"),
    nationalityOther: valueByHeader(row, "Q7 Nationality Detail"),
    religion: valueByHeader(row, "Q9 Religion"),
    ethnicity: valueByHeader(row, "Q10 Ethnicity"),
    highestEducation: valueByHeader(row, "Q11 Highest Level of Education"),
    currentlyEnrolled: valueByHeader(row, "Q12 Currently Enrolled"),
    schoolLevel: valueByHeader(row, "Q13 School Level"),
    placeOfSchool: valueByHeader(row, "Q14 Place of School"),
    reasonNotAttendingSchool: valueByHeader(row, "Q15 Reason for Not Attending School"),
    reasonNotAttendingSchoolOther: valueByHeader(row, "Q15 Other Reason Detail"),
    monthlyIncome: valueByHeader(row, "Q16 Monthly Income"),
    sourceOfIncome: valueByHeader(row, "Q17 Source of Income"),
    sourceOfIncomeOther: valueByHeader(row, "Q17 Source of Income Detail"),
    workStatus: valueByHeader(row, "Q18 Status of Work / Business"),
    placeOfWork: valueByHeader(row, "Q19 Place of Work / Business"),
    placeOfDelivery: valueByHeader(row, "Q20 Place of Delivery"),
    placeOfDeliveryOther: valueByHeader(row, "Q20 Place of Delivery Detail"),
    birthAttendant: valueByHeader(row, "Q21 Birth Attendant"),
    birthAttendantOther: valueByHeader(row, "Q21 Birth Attendant Detail"),
    immunization: valueByHeader(row, "Q22 Immunization"),
    livingChildren: valueByHeader(row, "Q23 Total Pregnancies"),
    livingChildrenAlive: valueByHeader(row, "Q23 Living Children"),
    familyPlanningUse: valueByHeader(row, "Q24 Family Planning Use"),
    familyPlanningUseOther: valueByHeader(row, "Q24 Family Planning Detail"),
    fpMethodSource: valueByHeader(row, "Q25 Source of FP Method"),
    fpMethodSourceOther: valueByHeader(row, "Q25 Source of FP Detail"),
    intentionToUseFp: valueByHeader(row, "Q26 Intention to Use FP"),
    intentionToUseFpDetail: valueByHeader(row, "Q26 FP Method / Reason Detail"),
    healthInsurance: valueByHeader(row, "Q27 Health Insurance"),
    healthInsuranceOther: valueByHeader(row, "Q27 Health Insurance Detail"),
    facilityVisitedPast12Months: valueByHeader(row, "Q28 Facility Visited in Past 12 Months"),
    facilityVisitedPast12MonthsOther: valueByHeader(row, "Q28 Facility Detail"),
    healthFacilityVisitReason: valueByHeader(row, "Q29 Health Facility Visit Reason"),
    healthFacilityVisitReasonOther: valueByHeader(row, "Q29 Visit Reason Detail"),
    disability: valueByHeader(row, "Q30 Disability"),
    soloParentStatus: valueByHeader(row, "Q31 Solo Parent"),
    registeredSeniorCitizen: valueByHeader(row, "Q32 Registered Senior Citizen"),
    registeredVoterBarangay: valueByHeader(row, "Q33 Registered Voter in Barangay"),
    lengthOfStayInBarangay: valueByHeader(row, "Q34 Length of Stay in Barangay"),
    previousResidenceFiveYears: valueByHeader(row, "Q35 Previous Residence - Five Years Ago"),
    previousResidenceSixMonths: valueByHeader(row, "Q36 Previous Residence - Six Months Ago"),
    residentType: valueByHeader(row, "Q37 Type of Resident"),
    dateOfTransfer: valueByHeader(row, "Q38 Date of Transfer"),
    reasonLeavingPreviousResidence1: valueByHeader(row, "Q39A Reason for Leaving Previous Residence"),
    reasonLeavingPreviousResidence1Other: valueByHeader(row, "Q39A Other Reason Detail"),
    reasonLeavingPreviousResidence2: valueByHeader(row, "Q39B Reason for Leaving Previous Residence"),
    reasonLeavingPreviousResidence2Other: valueByHeader(row, "Q39B Other Reason Detail"),
    reasonLeavingPreviousResidence3: valueByHeader(row, "Q39C Reason for Leaving Previous Residence"),
    reasonLeavingPreviousResidence3Other: valueByHeader(row, "Q39C Other Reason Detail"),
    returnToPreviousResidence: valueByHeader(row, "Q40 Return to Previous Residence"),
    returnToPreviousResidenceDetail: valueByHeader(row, "Q40 Return Date / Detail"),
    transferReason1: valueByHeader(row, "Q41A Reason for Transferring to Barangay"),
    transferReason1Other: valueByHeader(row, "Q41A Other Transfer Reason"),
    transferReason2: valueByHeader(row, "Q41B Reason for Transferring to Barangay"),
    transferReason2Other: valueByHeader(row, "Q41B Other Transfer Reason"),
    transferReason3: valueByHeader(row, "Q41C Reason for Transferring to Barangay"),
    transferReason3Other: valueByHeader(row, "Q41C Other Transfer Reason"),
    durationOfStayCurrentBarangay: valueByHeader(row, "Q42 Duration of Stay in Current Barangay"),
    durationOfStayCurrentBarangayDetail: valueByHeader(row, "Q42 Duration Date / Detail"),
    hasValidCtc: valueByHeader(row, "Q43A Has Valid CTC"),
    ctcIssuedInBarangay: valueByHeader(row, "Q43B CTC Issued in Barangay"),
    skillsTrainingInterest: valueByHeader(row, "Q44 Skills Development Training Interest"),
    skills: valueByHeader(row, "Q45 Skills"),
    skillsOther: valueByHeader(row, "Q45 Skills Detail"),
    occupation: valueByHeader(row, "Legacy Occupation (Supplemental)"),
  };
}

function ensureSingleHouseholdHead(members: RbimQuestionnairePayloadV1["members"]) {
  let headFound = false;
  members.forEach((member, index) => {
    if (isHead(member.relationshipToHead) && !headFound) {
      member.relationshipToHead = "Head";
      headFound = true;
      return;
    }
    if (isHead(member.relationshipToHead)) member.relationshipToHead = "Member";
    if (!headFound && index === members.length - 1) {
      members[0].relationshipToHead = "Head";
    }
  });
}

function hasCanonicalHeaders(matrix: string[][], expected: string[]) {
  const rows = matrix.filter((row) => row.some((cell) => clean(cell)));
  if (!rows.length) return false;
  const headers = new Set(rows[0].map(normalizeHeader));
  return expected.every((header) => headers.has(normalizeHeader(header)));
}

function matrixToNamedRows(matrix: string[][]) {
  const rows = matrix.filter((row) => row.some((cell) => clean(cell)));
  if (rows.length < 2) return [];
  const headers = rows[0].map((header) => clean(header));
  return rows.slice(1).map((values) => {
    const result: Record<string, string> = {};
    headers.forEach((header, index) => { if (header) result[normalizeHeader(header)] = clean(values[index]); });
    return result;
  });
}

function valueByHeader(row: Record<string, string>, header: string) {
  return clean(row[normalizeHeader(header)]);
}

function requiredImportText(value: string, fallback: string) {
  return clean(value) || fallback;
}

function parseBooleanCell(value: string) {
  return /^(?:true|1|yes|y)$/i.test(clean(value));
}

function parseNullableAge(value: string) {
  const age = Number(clean(value));
  return Number.isInteger(age) && age >= 0 && age <= 120 ? age : null;
}

function normalizeCalendarDate(value: string) {
  const parsed = parseBirthdate(value);
  return parsed ?? "";
}

function todayMmDdYyyy() {
  return formatDate(new Date());
}

function canonicalDateTime(value: string) {
  const text = clean(value);
  if (!text) return null;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function parseSharedStrings(bytes?: Uint8Array) {
  if (!bytes) return [];
  const document = new DOMParser().parseFromString(new TextDecoder().decode(bytes), "application/xml");
  return Array.from(document.querySelectorAll("si")).map((item) =>
    Array.from(item.querySelectorAll("t")).map((node) => node.textContent ?? "").join(""));
}

function columnIndex(letters: string) {
  let index = 0;
  for (const letter of letters.toUpperCase()) index = index * 26 + letter.charCodeAt(0) - 64;
  return Math.max(0, index - 1);
}

async function unzip(buffer: ArrayBuffer) {
  const view = new DataView(buffer);
  let eocd = -1;
  for (let offset = Math.max(0, buffer.byteLength - 65557); offset <= buffer.byteLength - 22; offset += 1) {
    if (view.getUint32(offset, true) === 0x06054b50) eocd = offset;
  }
  if (eocd < 0) throw new Error("The .xlsx file is not a valid ZIP workbook.");
  const entries = new Map<string, Uint8Array>();
  const decoder = new TextDecoder();
  let offset = view.getUint32(eocd + 16, true);
  const count = view.getUint16(eocd + 10, true);
  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) break;
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(new Uint8Array(buffer, offset + 46, fileNameLength));
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataOffset = localOffset + 30 + localNameLength + localExtraLength;
    const compressed = new Uint8Array(buffer.slice(dataOffset, dataOffset + compressedSize));
    if (method === 0) entries.set(name, compressed);
    else if (method === 8) entries.set(name, await inflateRaw(compressed));
    offset += 46 + fileNameLength + extraLength + commentLength;
  }
  return entries;
}

async function inflateRaw(bytes: Uint8Array) {
  if (typeof DecompressionStream === "undefined") throw new Error("This browser cannot read .xlsx files. Use CSV for legacy import.");
  const stableBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(stableBuffer).set(bytes);
  const stream = new Blob([stableBuffer]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
